import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Tabs, Button, Form, Layout, Tag, Row, Col, Typography, Spin, Table } from 'antd';
import { customBaseUrl, httpGet } from '../../store/http';
import { showLoader, hideLoader } from '../../utils/loader';
import Swal from 'sweetalert2';

const { TabPane } = Tabs;

const CustomerProfile = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('summary');
  
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [kycData, setKycData] = useState({});
  const [transactions, setTransactions] = useState([]);

  // Base Profile Info
  const fetchCustomerProfile = async () => {
    setLoading(true);
    showLoader();
    try {
      const response = await httpGet(`${customBaseUrl.auditUrl}/admin/customers/${id}`);
      setUserDetails(response?.data || response || {});
    } catch (e) {
      console.error(e);
      Swal.fire('Error', 'Failed to load customer profile context', 'error');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  useEffect(() => {
    if (id) {
      fetchCustomerProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Tab dynamic loaders based on active view block
  useEffect(() => {
    const fetchTabData = async () => {
      showLoader();
      try {
        if (activeTab === 'accounts' && !accounts.length) {
          const res = await httpGet(`${customBaseUrl.auditUrl}/admin/customers/accounts/${id}`);
          // Support generic paginated structures or pure arrays locally
          setAccounts(res?.data?.content || res?.data || res || []);
        }
        else if (activeTab === 'kyc' && !Object.keys(kycData).length) {
          const res = await httpGet(`${customBaseUrl.auditUrl}/admin/customers/kyc/${id}`);
          setKycData(res?.data || res || {});
        }
        else if (activeTab === 'transactions' && !transactions.length) {
          // If we have an account number mapped on the root user object
          const primaryAcc = userDetails?.accountNumber || userDetails?.primaryAccountNumber;
          if (primaryAcc) {
            const res = await httpGet(`${customBaseUrl.auditUrl}/admin/customers/enquiry/${primaryAcc}`);
            setTransactions(res?.data?.content || res?.data || res || []);
          }
        }
      } catch (e) {
        console.error(e);
        Swal.fire('Error', `Failed to load ${activeTab} data`, 'error');
      } finally {
        hideLoader();
      }
    };
    
    // Don't re-fetch aggressively if not needed unless user explicitly refreshes, though we can skip bounds here safely
    fetchTabData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, userDetails]);

  if (loading && !Object.keys(userDetails).length) {
    return (
      <Layout style={{ padding: '0 24px', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Spin size="large" />
      </Layout>
    );
  }

  // Pre-mapping schema attributes using null-safe chaining anticipating the expected backend nodes
  const fullName = userDetails?.fullName || `${userDetails?.firstName || ''} ${userDetails?.lastName || ''}`.trim() || 'Unknown';
  const typeLabel = userDetails?.userType || userDetails?.type || 'STANDARD';
  const primaryAcc = userDetails?.accountNumber || userDetails?.primaryAccountNumber || '—';
  
  return (
    <Layout style={{ padding: '0 24px' }}>
      <h1 style={{ marginBottom: 24, fontSize: '24px', fontWeight: '600' }}>Customer Report ({id})</h1>
      <Card
        title={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 600 }}>
              <span style={{ fontSize: '16px', fontWeight: 500 }}>{fullName}</span>
              <Tag color="cyan">{typeLabel.toUpperCase()}</Tag>
            </div>
            <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <div style={{ color: '#6b7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Primary Account</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', marginTop: '4px' }}>{primaryAcc}</div>
                </Col>
                <Col span={8}>
                  <div style={{ color: '#6b7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Linked Accounts</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', marginTop: '4px' }}>{accounts.length || '—'} Accounts</div>
                </Col>
                <Col span={8}>
                  <div style={{ color: '#6b7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>KYC Level</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', marginTop: '4px' }}><Tag color="blue">{kycData?.tierName || userDetails?.tierName || '—'}</Tag></div>
                </Col>
              </Row>
            </div>
          </div>
        }
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Personal Info" key="summary">
            {Object.keys(userDetails).length ? (
              <div style={{ padding: '16px 0' }}>
                <Row gutter={[24, 24]}>
                  {Object.entries(userDetails).map(([k, v]) => (
                    typeof v !== 'object' && v !== null && (
                      <Col span={8} key={k}>
                        <div style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', marginBottom: '4px' }}>{k}</div>
                        <div style={{ fontWeight: '500' }}>{String(v)}</div>
                      </Col>
                    )
                  ))}
                </Row>
              </div>
            ) : (
               <Typography.Paragraph>No Personal info mapped.</Typography.Paragraph>
            )}
          </TabPane>
          <TabPane tab="Accounts" key="accounts">
             <Table 
                dataSource={accounts} 
                rowKey={(r) => r.id || r.accountNumber || Math.random()} 
                loading={!accounts.length && loading}
             />
          </TabPane>
          <TabPane tab="KYC & Documents" key="kyc">
            <div style={{ padding: '16px 0' }}>
               <Row gutter={[24, 24]}>
                 {Object.entries(kycData).map(([k, v]) => (
                   typeof v !== 'object' && v !== null && (
                     <Col span={8} key={k}>
                       <div style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', marginBottom: '4px' }}>{k}</div>
                       <div style={{ fontWeight: '500' }}>{String(v)}</div>
                     </Col>
                   )
                 ))}
               </Row>
            </div>
          </TabPane>
          <TabPane tab="Transactions" key="transactions">
            <Typography.Paragraph>Enquiry details mapped here natively.</Typography.Paragraph>
            <Table 
              dataSource={transactions} 
              rowKey={(r) => r.id || r.transactionReference || Math.random()} 
              loading={!transactions.length && loading}
            />
          </TabPane>
        </Tabs>
      </Card>
    </Layout>
  );
};

export default CustomerProfile;
